import { jsx as _jsx } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import { tailwindMerge } from "@air/tailwind-variants";
import { forwardRef, memo, useCallback, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
export const GallerySelectableCardTrigger = /*#__PURE__*/ memo(/*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { className, confirmItemSelection, isSelectable, isHovering, isHighlighted, onClick, preSelectItem, style, ...restOfProps } = param;
    const { isAboveMediumScreen } = useBreakpointsContext();
    const isMouseDownRef = useRef(false);
    /**
       * We can not use the onClick event to trigger the selection event because onMouseUp may happen outside of the element
       * E.g. when user clicks down and scrolls the page - in that case, selection would be in a weird state, as if mouse button was still pressed
       */ const onMouseUp = useCallback(()=>{
        isMouseDownRef.current = false;
        flushSync(()=>{
            if (isAboveMediumScreen && isSelectable) {
                confirmItemSelection();
            }
        });
    }, [
        confirmItemSelection,
        isAboveMediumScreen,
        isSelectable
    ]);
    useEffect(()=>{
        if (isMouseDownRef.current) {
            window.addEventListener("mouseup", onMouseUp, {
                once: true
            });
        }
        return ()=>{
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [
        onMouseUp
    ]);
    const onMouseDown = useCallback((event)=>{
        flushSync(()=>{
            if (event.button === 0) {
                if (!isAboveMediumScreen || !isSelectable) return;
                isMouseDownRef.current = true;
                preSelectItem === null || preSelectItem === void 0 ? void 0 : preSelectItem();
                window.addEventListener("mouseup", onMouseUp, {
                    once: true
                });
            }
        });
    }, [
        isAboveMediumScreen,
        isSelectable,
        onMouseUp,
        preSelectItem
    ]);
    return /*#__PURE__*/ _jsx("button", {
        className: tailwindMerge("absolute inset-0 size-full cursor-default outline-none focus-visible:ring-2 focus-visible:ring-blue-9", className),
        "data-highlighted": isHighlighted,
        "data-hovering": isHovering,
        "data-is-selectable": isSelectable,
        "data-testid": "GALLERY_SELECTABLE_CARD_TRIGGER",
        onMouseDown: onMouseDown,
        onClick: !isAboveMediumScreen || !isSelectable ? onClick : undefined,
        ref: forwardedRef,
        style: {
            /**
             * Without this, the user can't select the card and drag on Firefox
             * @see https://air-labs-team.slack.com/archives/CSU3B8B3R/p1689598810694499
             */ minWidth: "-moz-available",
            ...style
        },
        ...restOfProps,
        children: /*#__PURE__*/ _jsx("span", {
            className: "sr-only",
            children: "Click to select"
        })
    });
}));
GallerySelectableCardTrigger.displayName = "GallerySelectableCardTrigger";
